import { Flex, Typography } from "antd"
import React from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { chipVariants, iconTileTints, neutral } from "../tokens"
import { WidgetFrame } from "../WidgetFrame"
import type { AlertItem, AlertSeverity, WidgetProps, WidgetState } from "../types"

const { Text } = Typography

const SEVERITY_TINTS: Record<AlertSeverity, { bg: string; fg: string }> = {
  critical: iconTileTints.error,
  warning: iconTileTints.warning,
  info: iconTileTints.teal,
}

const CriticalIcon = () => (
  <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
)

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8h.01M12 11v5" />
  </svg>
)

const SeverityIcon = ({ severity }: { severity: AlertSeverity }) =>
  severity === "info" ? <InfoIcon /> : <CriticalIcon />

const AlertRow = ({ item }: { item: AlertItem }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const severity = item.severity ?? "warning"
  const tint = SEVERITY_TINTS[severity] ?? SEVERITY_TINTS.warning
  const target = item.drill_to

  const handleOpen = () => {
    if (target) navigate(target)
  }

  return (
    <Flex
      data-testid={`alert-item-${item.id}`}
      data-severity={severity}
      role={target ? "button" : undefined}
      tabIndex={target ? 0 : undefined}
      onClick={target ? handleOpen : undefined}
      onKeyDown={
        target
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                handleOpen()
              }
            }
          : undefined
      }
      align="center"
      gap={12}
      style={{
        padding: "12px 14px",
        border: `1px solid ${neutral.border}`,
        borderRadius: 10,
        background: neutral.surface,
        cursor: target ? "pointer" : "default",
      }}
    >
      <div
        role="img"
        aria-label={t(`portfolio.alert.severity.${severity}`, severity)}
        data-testid="alert-severity-icon"
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          flex: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: tint.bg,
          color: tint.fg,
        }}
      >
        <SeverityIcon severity={severity} />
      </div>
      <Text strong ellipsis={{ tooltip: item.title }} style={{ flex: 1, minWidth: 0 }}>
        {item.title}
      </Text>
      {typeof item.count === "number" && (
        <span
          data-testid="alert-count"
          style={{
            flex: "none",
            fontSize: 12,
            fontWeight: 700,
            padding: "1px 9px",
            borderRadius: 999,
            background: chipVariants.muted.bg,
            color: chipVariants.muted.fg,
          }}
        >
          {item.count}
        </span>
      )}
    </Flex>
  )
}

export const AlertWidget = ({
  widget,
  payload,
  state,
  onRefresh,
  onExpand,
  onDrill
}: WidgetProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const items = payload?.alert?.items
  const title = widget.config?.title ?? widget.title

  let effectiveState: WidgetState
  if (state) {
    effectiveState = state
  } else if (!items) {
    effectiveState = "loading"
  } else if (items.length === 0) {
    effectiveState = "empty"
  } else {
    effectiveState = "ready"
  }

  return (
    <WidgetFrame
      title={title}
      state={effectiveState}
      capabilities={widget.capabilities}
      widgetId={widget.id}
      drillTarget={widget.drill_to}
      payload={payload}
      emptyText={t("portfolio.alert.allClear", "All clear — nothing needs attention")}
      onRefresh={onRefresh}
      onExpand={onExpand}
      onDrill={(target) => (onDrill ? onDrill(target) : navigate(target))}
    >
      <Flex vertical gap={8}>
        {(items ?? []).map((item) => (
          <AlertRow key={item.id} item={item} />
        ))}
      </Flex>
    </WidgetFrame>
  )
}
